
import {
  computed, defineComponent, nextTick, PropType, ref,
} from 'vue';
import { useI18n } from 'vue-i18n';
import InputSwitch from 'primevue/inputswitch';
import Dropdown from 'primevue/dropdown';

export default defineComponent({
  name: 'TierAutoUpgradeBlock',

  components: { InputSwitch, Dropdown },

  props: {
    /**
     * The tier the user has chosen for their subscription. Used as the
     * floor for the plafond dropdown : the customer can only cap at a
     * strictly higher tier (`currentTier + 1` → grid max).
     */
    currentTier: {
      type: Number as PropType<number>,
      required: true,
    },
    /**
     * The full pricing grid. Tiers strictly above `currentTier` populate
     * the plafond dropdown. When empty (shop already on the highest tier),
     * the block degrades to a "maxedOut" message.
     */
    tiers: {
      type: Array as PropType<Array<{ threshold: number }>>,
      default: () => [],
    },
    /** v-model: `tierAutoUpgradeEnabled`. Defaults to true. */
    enabled: {
      type: Boolean,
      default: true,
    },
    /**
     * v-model: `tierAutoUpgradeMax`. Initialised by the parent
     * (PlanComparator) — the block is a pure presenter and never
     * self-initialises this value.
     */
    maxTier: {
      type: Number as PropType<number>,
      default: 0,
    },
  },

  emits: ['update:enabled', 'update:maxTier'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const editing = ref(false);
    const dropdownRef = ref<any>(null);

    const formatNumber = (n: number) => new Intl.NumberFormat('fr-FR').format(n);

    // Visible as soon as a tier is selected — the toggle is always usable.
    const visible = computed(() => props.currentTier > 0);

    // Eligible plafond tiers = strictly above the current tier. Sorted
    // ascending for predictable dropdown order.
    const maxTierOptions = computed(() => {
      const sorted = [...props.tiers]
        .map((tier) => tier.threshold)
        .filter((th) => th > props.currentTier)
        .sort((a, b) => a - b);
      return sorted.map((th) => ({
        value: th,
        label: t('billing.tierAutoUpgrade.option', { count: formatNumber(th) }),
      }));
    });

    const hasOptions = computed(() => maxTierOptions.value.length > 0);

    const onToggleEnabled = (v: boolean) => {
      emit('update:enabled', v);
    };
    const onMaxTierChange = (v: number) => {
      emit('update:maxTier', v);
      editing.value = false;
    };

    // Swap the hint paragraph for the dropdown and open it on the next
    // tick — saves a second click for the user.
    const startEditing = async () => {
      editing.value = true;
      await nextTick();
      dropdownRef.value?.show?.();
    };

    return {
      t,
      visible,
      editing,
      dropdownRef,
      maxTierOptions,
      hasOptions,
      formatNumber,
      onToggleEnabled,
      onMaxTierChange,
      startEditing,
    };
  },
});
